/**
 * Local calendar date arithmetic. NFR-08.
 *
 * ## Why this file exists
 *
 * `new Date('2026-08-17')` parses as UTC midnight. Formatting that back with
 * `toISOString()` in Istanbul gives '2026-08-17', but in Los Angeles gives
 * '2026-08-16'. A period tracker that shifts a recorded date when the user
 * travels — or merely when they cross into DST — is broken in a way that is
 * both hard to notice and impossible to trust afterwards.
 *
 * So: dates are strings here. All arithmetic is done on the calendar parts, in
 * UTC internally purely as a stable integer axis, and never surfaced as a
 * `Date`. Nothing in this module reads the system timezone, which is why the
 * whole of `domain/` produces identical output under any TZ.
 *
 * ## Rules
 *
 *  - `toISOString()` is banned in this codebase for producing a date key.
 *  - `Date` objects do not cross a module boundary.
 *  - The only function that reads the clock is `today()`, and it takes the
 *    clock as an argument so every caller in `domain/` stays pure.
 */
import type { ISODate } from './types'

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/
const MS_PER_DAY = 86_400_000

export function isISODate(value: unknown): value is ISODate {
  if (typeof value !== 'string') return false
  const m = ISO_DATE.exec(value)
  if (!m) return false
  // Reject impossible dates like '2026-02-30' that the regex alone would pass.
  const [, y, mo, d] = m
  const date = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)))
  return (
    date.getUTCFullYear() === Number(y) &&
    date.getUTCMonth() === Number(mo) - 1 &&
    date.getUTCDate() === Number(d)
  )
}

function assertISODate(value: string): asserts value is ISODate {
  if (!isISODate(value)) throw new TypeError(`Not an ISO date: ${JSON.stringify(value)}`)
}

const pad2 = (n: number) => String(n).padStart(2, '0')

export function makeISODate(year: number, month1: number, day: number): ISODate {
  const value = `${String(year).padStart(4, '0')}-${pad2(month1)}-${pad2(day)}`
  assertISODate(value)
  return value
}

/**
 * The user's current local calendar date.
 *
 * Reads local calendar parts — never `toISOString()`. Takes the clock as a
 * parameter so callers can be tested deterministically.
 */
export function today(now: Date = new Date()): ISODate {
  return makeISODate(now.getFullYear(), now.getMonth() + 1, now.getDate())
}

/** Internal: a stable integer axis for arithmetic. Not a point in time. */
function toEpochDay(date: ISODate): number {
  assertISODate(date)
  const [y, m, d] = date.split('-').map(Number) as [number, number, number]
  return Date.UTC(y, m - 1, d) / MS_PER_DAY
}

function fromEpochDay(epochDay: number): ISODate {
  const dt = new Date(epochDay * MS_PER_DAY)
  return makeISODate(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate())
}

export function addDays(date: ISODate, days: number): ISODate {
  return fromEpochDay(toEpochDay(date) + days)
}

/** Whole days from `a` to `b`. Negative when `b` precedes `a`. */
export function daysBetween(a: ISODate, b: ISODate): number {
  return toEpochDay(b) - toEpochDay(a)
}

export function compareDates(a: ISODate, b: ISODate): number {
  // 'YYYY-MM-DD' sorts lexicographically in chronological order, which is also
  // why IndexedDB range queries over date keys are free (data model Section 3).
  return a < b ? -1 : a > b ? 1 : 0
}

export const isBefore = (a: ISODate, b: ISODate) => compareDates(a, b) < 0
export const isAfter = (a: ISODate, b: ISODate) => compareDates(a, b) > 0

/** Day of week, 0 = Monday .. 6 = Sunday. Monday-first because the calendar is. */
export function dayOfWeek(date: ISODate): number {
  const dt = new Date(toEpochDay(date) * MS_PER_DAY)
  return (dt.getUTCDay() + 6) % 7
}

export function startOfMonth(date: ISODate): ISODate {
  const [y, m] = date.split('-').map(Number) as [number, number]
  return makeISODate(y, m, 1)
}

export function daysInMonth(year: number, month1: number): number {
  return new Date(Date.UTC(year, month1, 0)).getUTCDate()
}

export function endOfMonth(date: ISODate): ISODate {
  const [y, m] = date.split('-').map(Number) as [number, number]
  return makeISODate(y, m, daysInMonth(y, m))
}

export function addMonths(date: ISODate, months: number): ISODate {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number]
  const total = y * 12 + (m - 1) + months
  const year = Math.floor(total / 12)
  const month1 = (total % 12) + 1
  // Clamp: 31 January + 1 month is 28/29 February, not 3 March.
  return makeISODate(year, month1, Math.min(d, daysInMonth(year, month1)))
}

/** Every date from `from` to `to`, inclusive. */
export function datesInRange(from: ISODate, to: ISODate): ISODate[] {
  const out: ISODate[] = []
  for (let d = toEpochDay(from); d <= toEpochDay(to); d++) out.push(fromEpochDay(d))
  return out
}
